import { useCallback, useEffect, useRef, useState } from 'react';

import {
  INITIAL_VOICE_GATE,
  MOCK_SESSION_ID,
  createPitchTrackerConfig,
  createPracticeBridge,
  isRunningInNativeApp,
  native,
  stepVoiceGate,
  useRafThrottle,
} from '@muzly/shared';
import type {
  NativeF0Frame,
  PitchTrackerEvent,
  VoiceGateState,
} from '@muzly/shared';
import type { NativeRecording } from '@muzly/shared';

// 배럴이 아니라 모듈을 직접 집는다 — `record/index.ts` 는 `diagnosisProgress` 를 통해
// 다시 `../capture` 를 가리켜서, 배럴로 들어오면 capture↔record 순환이 된다.
import { CAPTURE_GAP_MS, voicedSpanSec } from './framesToClips';
import { appendLivePoint, growSungRange } from './livePoints';
import { INITIAL_PROBE_LATCH, stepProbeLatch } from './probeLatch';
import {
  BROWSER_MIC_SESSION_ID,
  PCM_TARGET_SAMPLE_RATE,
  createBrowserSourceModule,
  createPcmRecorder,
} from '../../../audio';
import { nativeReadDeps, readTakeBytes } from '../record/readTakeBytes';

import type { SungRange } from './livePoints';
import type { ProbeLatch } from './probeLatch';
import type { MicGraph, PcmTake } from '../../../audio';
import type { PitchGraphPoint } from '../../../components';

/**
 * useVoiceCapture — 온보딩 녹음의 캡처 seam(IO). 마이크를 직접 잡지 않고 **네이티브
 * PitchTracker 엔진**의 `pitch_frame`(NativeF0Frame) 스트림을 받아 라이브 값을 흘리고,
 * stop()에서 수집 프레임을 그대로 넘긴다 — 해석(clip 분해·MPT)은 framesToClips 몫이다.
 *
 * 전송(transport)은 usePitchSession과 동일한 이중 경로다:
 *   - 네이티브 앱(WebView, 프로덕션): native.on('pitchTrackerEvent') + native.invoke(startPractice)
 *   - 브라우저: createPracticeBridge(`app/audio`의 마이크 소스 모듈) — 디버그 콘솔이
 *     쓰는 것과 같은 모듈이라 실제 목소리가 그대로 들어온다. 마이크를 못 열면 그 모듈이
 *     `error` 이벤트를 내고 화면이 에러 단계로 간다(조용히 합성으로 대체하지 않는다).
 *
 * **출처(`CaptureSource`)는 전송을 고른 자리가 아니라 `session_started`가 정한다** —
 * `sourceOfSession` 주석 참고.
 * 세션 상태머신(usePitchSession)은 멀티클릭 레이스 방어가 목적인데, 온보딩 캡처는 버튼을
 * 녹음 중 비활성화하는 단선형 start→collect→stop이라 그 보증이 불필요 → 프레임 수집에
 * 특화한 얇은 훅으로 같은 프리미티브(브리지/mock/native)만 재사용한다.
 *
 * 종료는 **사용자만** 한다. 자동 종료가 없는 이유는 테스트 ①(`/a/` 길게)이 숨이 다할
 * 때까지 재는 시험이라서다 — 훅이 목표 시간에 끊으면 그 시점부터는 MPT가 아니라 훅의
 * 상수를 재게 된다. 일시정지도 같은 이유로 없다(숨은 일시정지할 수 없다).
 *
 * 불변식(GOAL.md): **네이티브 경로에서는** JS로 Analysis Frame(f0/rms/voiced)만 건너온다 —
 * raw PCM 없음. 마이크 권한/오디오세션은 네이티브가 소유하므로 WebView getUserMedia 권한
 * 문제가 없다. 브라우저 경로에는 네이티브 엔진이 아예 없어 그 불변식이 걸리지 않는다 —
 * 거기서는 f0도 PCM도 JS가 다룬다(`app/audio/pcmRecorder.ts` 주석).
 */

/**
 * `blocked` 는 OS 설정에서 이미 꺼 둔 상태다 — 앱 안에서 다시 물어도 시트가 안 뜨므로
 * `denied` 와 달리 **설정으로 보내는 것**이 유일한 복구다.
 */
export type MicError = 'denied' | 'blocked' | 'unsupported';

/**
 * 프레임이 실제로 어디서 왔는지. **`isRunningInNativeApp()`을 나중에 다시 묻지 않고**
 * 세션을 열 때 실제로 고른 전송을 그대로 남긴다 — 나중에 다시 물으면 그 사이 값이 달라져도
 * 알 수 없고, mock 세션이 조용히 "native"로 기록될 수 있다.
 *
 * - `native` — 네이티브 PitchTracker(MPM). 프로덕션 WebView 경로.
 * - `mic` — 브라우저 `getUserMedia` + JS ACF(`micFrames.ts`). **실제 목소리**이지만
 *   추정기가 네이티브와 다르다 — 두 경로의 수치를 나란히 비교하면 안 된다.
 * - `mock` — 합성 프레임. 마이크를 못 열었을 때만 떨어지는 개발용 폴백이라 **측정이
 *   아니다**(회차로 저장되지 않는다: `saveDiagnosis`).
 */
export type CaptureSource = 'native' | 'mic' | 'mock';

/**
 * 어느 모듈이 **실제로** 열렸나 — `session_started`가 들고 온 세션 식별자로 정한다.
 *
 * 코드가 어디서 도는지(`isRunningInNativeApp()`)로 정하면 안 된다. 모바일 쪽
 * `resolvePitchTrackerModule()`은 네이티브 모듈이 등록돼 있지 않으면(네이티브 빌드 없이
 * Metro 셸만 띄운 개발 환경) 조용히 합성 모듈로 떨어진다. 그 회차를 'native'로 적으면
 * 합성 프레임이 **실측으로 저장되고**(`saveDiagnosis`), mock을 알리는 자리들
 * (`ResultReport`의 안내, `VoiceRecordStep`의 에러 문구)이 전부 침묵한다.
 *
 * 프레임의 `algorithm`은 근거가 못 된다. 그건 **어느 추정기가 돌았나**이지 프레임이 어디서
 * 왔나가 아니고, 두 값이 실제로 어긋나 있다: 네이티브는 C enum 정수를 싣고(`algorithm: 0`,
 * 시뮬레이터 실측) mock은 config가 고른 문자열(기본 `'mpm'`)을 싣는다. 지금은 그 어긋남
 * 덕에 구분이 되지만, 그건 계약 위반(`FrameAlgorithm`은 문자열 union)이라 여기서 그걸
 * 근거로 쓰면 **버그를 계약으로 굳히는** 셈이다 — `f0frame-contract.md` §4.2.
 */
export const sourceOfSession = (sessionId: string): CaptureSource => {
  if (sessionId === MOCK_SESSION_ID) {
    return 'mock';
  }
  return sessionId === BROWSER_MIC_SESSION_ID ? 'mic' : 'native';
};

export interface LiveSample {
  /** 지금까지의 발성 길이(초) — 최종 MPT와 **같은 함수**로 잰다. */
  voicedSec: number;
  /** 그래프가 그릴 롤링 창. 이 훅이 프레임의 **단일 구독처**다. */
  points: PitchGraphPoint[];
  /** 이번 테이크에서 부른 음의 폭 — 롤링 창이 아니라 **전체**다(축이 튀지 않게). */
  sung: SungRange | null;
  /**
   * 이 테이크의 시간 원점 — `markTake()` 뒤 **첫 프레임**의 타임스탬프(무성이어도
   * 첫 프레임이다). 아직 한 프레임도 없으면 null.
   *
   * 롤링 창의 첫 점(`points[0].t`)과 다르다: 창은 밀려나며 앞이 잘리는데 원점은 안 변한다.
   * 그리고 이 값이 **채점이 쓰는 앵커와 같은 값**이어야 한다 — `scoreSong`은
   * `referenceStartMs ?? frames[0].timestampMs`를 쓰고 아무도 앞의 것을 안 넘기므로,
   * 실제 앵커는 자르지 않은 프레임 배열의 첫 원소다. 화면이 다른 원점을 쓰면 가이드는
   * 맞았다는데 점수는 틀린 상태가 나온다.
   */
  takeStartMs: number | null;
  /**
   * 일시 중단에서 **다시 듣기 시작한 시각** — 재개 뒤 첫 프레임의 타임스탬프.
   * 한 번도 안 멈췄으면 null.
   *
   * 무발성 자동 종료가 이 값을 바닥으로 쓴다. 세션은 멈춘 동안에도 열려 있어
   * 캡처 타임라인이 계속 흐르므로, 이 바닥이 없으면 30초 멈췄다 재개한 순간
   * "30초째 조용하다"로 읽혀 테이크가 곧바로 끊긴다.
   */
  resumedAtMs: number | null;
  /**
   * 엔진은 음을 들었는데 **게이트가 레벨 때문에 버린** 마지막 프레임의 시각.
   * 한 번도 없었으면 null.
   *
   * "목소리가 작아요"와 "감지되지 않았어요"를 가르는 유일한 근거다(시안 §5.4:
   * `884:8299` vs `884:8720`). 둘 다 무성 프레임으로 보이지만 처방이 반대다 —
   * 하나는 더 크게 내라는 말이고 하나는 소리를 내라는 말이다.
   */
  faintAtMs: number | null;
  /**
   * 음성 게이트가 이 방의 조용함을 **정했나**. 정하려면 300ms 창의 중앙값이
   * `-40dBFS`보다 조용해야 한다(`FLOOR_ROOM_MAX_DB`).
   *
   * 오래도록 안 정해지는 동안 엔진도 음을 못 들었다면, 그 방은 측정에 쓸 수 없을 만큼
   * 시끄럽다는 뜻이다(`roomNoise.ts`).
   */
  floorLocked: boolean;
  /**
   * 게이트가 잡은 방 소음 바닥(dBFS) — `floorLocked`의 값. 300ms 창의 중앙값이라
   * 한 번 정해지면 테이크 내내 안 변한다. 못 잡았으면 null — null도 "못 잡았다"는
   * 잰 사실이라 제출 `capture.noiseFloorDbfs`에 그대로 싣는다.
   */
  noiseFloorDbfs: number | null;
  /**
   * 방금 들어온 프레임의 rms(dBFS) — 게이트 **앞**의 값이다.
   *
   * 계측 전용. `noiseFloorDbfs`와 나란히 놓고 봐야 "이 소리가 이 방 기준으로 통과선
   * (`floor + NOISE_MARGIN_DB`)을 넘나"를 화면에서 바로 읽을 수 있다. 둘 중 하나만
   * 보면 게이트가 버린 이유를 못 가른다(2026-09-16 노래 집음 조사).
   */
  lastRmsDbfs: number | null;
  /**
   * 이 테이크에서 온 **프레임 아닌** 이벤트의 흔적 — 계측 전용.
   *
   * 온보딩 캡처는 `session_started`·`error`·`pitch_frame` 외의 이벤트를 전부 버린다.
   * 그래서 오디오 세션이 인터럽트되거나(`interruption_started`) 스트림이 끊겨도
   * (`stream_discontinuity`) 화면에는 "프레임이 안 온다"로만 보이고, 시계는 계속 돈다.
   * 반주까지 같이 끊기는 증상은 정확히 그 모양이라 이 둘을 세어 화면에 올린다
   * (2026-09-16 노래 집음 조사).
   */
  interruptCount: number;
  discontinuityCount: number;
  /** 마지막으로 온 프레임 아닌 이벤트 이름. 없으면 null. */
  lastEvent: string | null;
  /**
   * 이 테이크에서 엔진이 **표시용 스무딩 값**(`smoothedF0Hz`)을 한 번이라도 실어 왔나.
   *
   * 안 오면 화면은 raw midi로 떨어지고(`livePoints.ts` `displayMidi`), raw에는 네이티브의
   * 옥타브 억제가 안 걸려 있다(설계상 두 계열 분리) — 즉 "표시 음이 옥타브 튄다"의 후보
   * 하나가 이 한 비트로 갈린다. 한 번 참이면 유지한다: 프레임마다 깜빡이면 읽을 수 없다.
   */
  smoothedSeen: boolean;
  /**
   * 계측 오버레이의 `onset`·`oct` 재료 — 엔진 첫 유성·게이트 첫 채택 시각과 raw↔smoothed
   * 옥타브 어긋남 수(`probeLatch.ts`). 오버레이가 꺼져 있어도 모은다(프레임당 O(1)).
   */
  probe: ProbeLatch;
}

export interface StartOptions {
  /**
   * 이 테이크 동안 반주가 같이 나가나(노래 진단). 네이티브 세션 모드를 가르는 값이라
   * 세션 시작에만 먹는다(`AudioInputConfig.backingTrack`).
   */
  backingTrack?: boolean;
}

export interface VoiceCapture {
  live: LiveSample;
  error: MicError | null;
  /** 지금 세션이 어느 전송으로 열렸나. 시작 전이면 null. */
  source: CaptureSource | null;
  start: (options?: StartOptions) => void;
  /** 세션 종료 + 수집 프레임(불변 복사본). 해석은 framesToClips가 한다. */
  stop: () => NativeF0Frame[];
  /**
   * 방금 테이크의 원음. **`mic` 경로에서만** 값이 있다 — 네이티브는 raw PCM을 JS로
   * 넘기지 않고(GOAL.md), mock은 애초에 소리가 없다. `stop()` 뒤에 부른다.
   */
  takePcm: () => PcmTake | null;
  /**
   * 네이티브가 기기에 쓴 원음 파일. **`native` 경로에서만** 값이 있다 — 브라우저는
   * 파일이 아니라 표본을 들고 있고(`takePcm`), mock은 소리가 없다.
   *
   * 표본이 아니라 **경로와 계수**만 온다. 그래서 이 경로에서도 "원음이 나온다"가
   * 성립하면서 GOAL.md 불변식이 그대로 남는다. `stop()` 뒤에 부른다 — 네이티브가
   * `stop()`에서 헤더를 마감한다.
   */
  takeNativeRecording: () => Promise<NativeRecording | null>;
  /** 테이크 끝의 게이트 바닥(dBFS). 못 잡았으면 `null`. `stop()` 직후 부른다. */
  readNoiseFloorDbfs: () => number | null;
  /** 끝난 테이크의 바이트. 브라우저·mock·읽기 실패는 전부 `null` 이다. */
  readNativeRecording: (uri: string) => Promise<Uint8Array | null>;
  /** 기기에 남은 테이크 파일 삭제. 실패해도 던지지 않는다. */
  deleteNativeRecording: (uri: string) => Promise<void>;
  /**
   * 측정을 **잠깐 멈춘다** — 세션은 열어 둔 채 들어오는 프레임을 버리고 원음 녹음기를
   * 뗀다(시안 §5.1 `일시 중단`).
   *
   * 세션을 끊지 않는 이유: `timestampMs`는 세션의 캡처 타임라인이라(엔진 계약) 새 세션의
   * 프레임을 같은 테이크에 이어 붙이면 시간축이 뒤로 점프한다. 녹음기를 떼는 이유는
   * 반대다 — 멈춘 동안의 방 소리가 파일에 남으면 "멈춤"이 거짓말이 된다.
   *
   * 마이크 자체는 열려 있다(기기 표시등도 켜져 있다). 멈춘 동안의 소리는 어디에도
   * 남지 않지만, 장치를 껐다고 말하지는 않는다.
   */
  pause: () => void;
  /** 다시 듣기 시작한다. 모아 둔 프레임은 그대로 있고 그 뒤에 이어 붙는다. */
  resume: () => void;
  /**
   * **테이크가 여기서 시작한다** — 그동안 모은 프레임·곡선·원음 표본을 버리고 다음
   * 프레임을 `takeStartMs`로 삼는다. 게이트는 **남긴다**: 세션은 카운트다운 때 열려
   * 3초 동안 방을 재는데(`voice-gate.ts` CALIBRATION_MS), 그 바닥이 이 테이크의 것이다.
   *
   * 왜 세션 시작과 테이크 시작을 가르나: 게이트는 첫 300ms 의 rms 중앙값으로 방을
   * 재고 목소리가 섞인 창은 버리고 다시 잰다. 테이크와 같이 열면 노래를 아는 사람은
   * 첫 프레임부터 부르므로 창마다 오염돼 바닥을 영영 못 잠그고, 잠글 때까지 반주를
   * 미루면 사용자가 숨을 고르는 순간에야 반주가 "중간에" 나온다(2026-09-16 실기기).
   */
  markTake: () => void;
  reset: () => void;
}

const INITIAL_LIVE: LiveSample = {
  voicedSec: 0,
  points: [],
  sung: null,
  takeStartMs: null,
  resumedAtMs: null,
  faintAtMs: null,
  floorLocked: false,
  noiseFloorDbfs: null,
  lastRmsDbfs: null,
  interruptCount: 0,
  discontinuityCount: 0,
  lastEvent: null,
  smoothedSeen: false,
  probe: INITIAL_PROBE_LATCH,
};

const errorFromCode = (code: string): MicError =>
  code === 'microphone_permission_denied' ? 'denied' : 'unsupported';

/**
 * 원음으로 보관하는 마지막 구간(초).
 *
 * 20초인 이유는 **분석 창과 같아야 해서**다: 호흡 안정도가 최대 20초를 보고, 내보내는
 * JSON도 20초를 싣기로 했다. 지속음 진단은 30초까지 녹음할 수 있으므로(`SUSTAIN_LIMIT_SEC`)
 * 넘친 앞부분은 버려진다 — 그때 `PcmTake.trimmed`가 true로 서서, 받은 쪽이 이게 테이크
 * 전체가 아니라 **끝의 20초**임을 알 수 있다.
 */
export const PCM_KEEP_SEC = 20;

/**
 * 테이크에서 **분석에 쓰는 마지막 구간**(초)을 잘라낸다.
 *
 * 지속음은 30초까지 녹음하지만 원음 보관은 마지막 20초다(`PCM_KEEP_SEC`). 프레임을 안
 * 자르면 화면이 "29.6초 발성"이라 말하는데 그 말을 뒷받침하는 소리는 20초치만 남는다 —
 * 보고한 값과 내보낸 증거가 다른 상태다. 두 창을 같은 값으로 묶어 그 상태를 없앤다.
 *
 * 창보다 짧은 테이크는 **손대지 않는다**(같은 배열 참조를 그대로 낸다): 음역 20초·노래
 * ~7초는 애초에 창 안이고, 거기서 앞을 자르면 노래 채점의 시간 앵커(첫 프레임)가 밀린다.
 *
 * 재는 것은 벽시계가 아니라 **실제로 담은 시간**이다. 일시 중단은 세션을 열어 둔 채
 * 프레임만 버리므로(`pause`) 멈춘 구간은 프레임 사이의 큰 간격으로 남는다. 벽시계로
 * 자르면 그 빈 구간이 창을 먹어 15초 부르고 10초 멈춘 테이크에서 앞의 10초가 조용히
 * 사라진다. 원음 녹음기도 표본 기준(= 담은 시간)으로 20초를 들고 있으므로, 이렇게 세야
 * 프레임과 원음이 **같은 구간**을 가리킨다.
 */
export const withinAnalysisWindow = (
  frames: NativeF0Frame[],
  keepSec = PCM_KEEP_SEC,
): NativeF0Frame[] => {
  if (frames.length === 0) {
    return frames;
  }
  const keepMs = keepSec * 1000;
  // 벽시계로도 창 안이면 담긴 시간은 그보다 짧거나 같다 — 걸어 볼 것도 없다.
  // 이 함수는 **프레임마다** 불리므로(`onEvent`가 voicedSec을 낼 때) 짧은 테이크에서
  // 배열 전체를 매번 되짚지 않게 여기서 끊는다.
  if (frames[frames.length - 1].timestampMs - frames[0].timestampMs <= keepMs) {
    return frames;
  }
  let captured = 0;
  let cut = 0;
  for (let i = frames.length - 1; i > 0; i -= 1) {
    const delta = frames[i].timestampMs - frames[i - 1].timestampMs;
    // 프레임 간격보다 훨씬 큰 틈은 담긴 소리가 아니라 **멈춰 있던 구간**이다.
    const step = delta > CAPTURE_GAP_MS ? 0 : delta;
    // 창을 **넘길 때** 자른다 — 딱 맞는 테이크(음역 20초)는 통째로 남아야 한다.
    if (captured + step > keepMs) {
      cut = i;
      break;
    }
    captured += step;
  }
  return cut === 0 ? frames : frames.slice(cut);
};

export const useVoiceCapture = (): VoiceCapture => {
  const [live, setLive] = useState<LiveSample>(INITIAL_LIVE);
  const [error, setError] = useState<MicError | null>(null);
  const [source, setSource] = useState<CaptureSource | null>(null);

  const framesRef = useRef<NativeF0Frame[]>([]);
  // 원음 녹음기는 마운트 동안 하나만 둔다 — 테이크마다 새로 만들면 마이크가 열린 뒤에
  // 붙일 대상이 바뀌어 첫 청크를 놓친다.
  const pcmRef = useRef(createPcmRecorder(PCM_KEEP_SEC));
  const liveRef = useRef<LiveSample>(INITIAL_LIVE);
  const gateRef = useRef<VoiceGateState>(INITIAL_VOICE_GATE);
  // 멈춘 동안 들어오는 프레임은 버린다. state가 아니라 ref인 이유: `onEvent`는
  // 세션 시작 때 한 번 묶이므로, state를 읽으면 묶인 시점의 값에 갇힌다.
  const pausedRef = useRef(false);
  // 재개 뒤 **첫** 프레임에 표식을 찍으려는 대기 플래그.
  const resumeMarkPendingRef = useRef(false);
  // Transport bound at start() (native RPC vs mock bridge): stop tears down the
  // listener/session. Nothing else touches the transport after start.
  const transportRef = useRef<{ stop: () => void } | null>(null);
  // 열려 있는 마이크 그래프. 일시 중단은 여기서 녹음기만 뗐다가 다시 붙인다.
  const micGraphRef = useRef<MicGraph | null>(null);

  // session_started에서도 불린다 — 그래프 창까지 여기서 비워야 이전 세션의 곡선이
  // 새 세션에 이어 붙지 않는다(예전엔 그래프가 자기 버퍼를 따로 비웠다).
  const resetAccumulators = useCallback((): void => {
    framesRef.current = [];
    pcmRef.current.reset();
    liveRef.current = INITIAL_LIVE;
    // 잡음 플로어는 방마다 다르고 세션마다 다시 잡는다 — 이전 테이크의 플로어를
    // 들고 가면 조용한 방에서 시작한 다음 테이크가 통째로 무성이 된다.
    gateRef.current = INITIAL_VOICE_GATE;
  }, []);

  const onEvent = useCallback(
    (event: PitchTrackerEvent): void => {
      if (event.type === 'session_started') {
        // 출처는 **여기서** 확정된다 — 어느 모듈이 열렸는지는 세션이 말한다.
        setSource(sourceOfSession(event.session.sessionId));
        resetAccumulators();
        return;
      }
      if (event.type === 'error') {
        setError(errorFromCode(event.error.code));
        return;
      }
      if (event.type !== 'pitch_frame') {
        // 계측: 버리기 전에 센다. 무엇을 버렸는지 화면이 말해야 "프레임이 왜 멈췄나"에
        // 답할 수 있다(`LiveSample.interruptCount` 주석).
        if (event.type !== 'input_level') {
          liveRef.current = {
            ...liveRef.current,
            lastEvent: event.type,
            interruptCount:
              liveRef.current.interruptCount +
              (event.type === 'interruption_started' ? 1 : 0),
            discontinuityCount:
              liveRef.current.discontinuityCount +
              (event.type === 'stream_discontinuity' ? 1 : 0),
          };
        }
        return;
      }
      // **유·무성 최종 판정은 여기 한 번뿐이다.** 이 방의 조용함 대비로 다시 재고
      // (voice-gate: 음정 있는 암소음은 confidence로도 절대 dB로도 안 걸러진다),
      // 걸러진 프레임은 무성 프레임의 모습 그대로 만들어 아래로 흘린다. 그래야 그래프·
      // MPT·음역·채점이 전부 같은 판정을 쓴다 — 소비처마다 따로 거르면 화면은 소리를
      // 봤다는데 리포트는 못 봤다는 상태가 생긴다.
      // 멈춰 있으면 게이트에도 안 넣는다 — 잡음 플로어는 이어서 재야 할 값이라
      // 멈춘 동안의 방 소리로 갱신되면 재개 직후 판정이 그 소리를 기준으로 돈다.
      if (pausedRef.current) {
        return;
      }
      const gated = stepVoiceGate(gateRef.current, event.frame);
      gateRef.current = gated.state;
      const frame: NativeF0Frame = gated.isVoice
        ? event.frame
        : { ...event.frame, voiced: false, f0Hz: null, midi: null };
      framesRef.current.push(frame);
      // 라이브 값과 최종 MPT를 **같은 함수**로 낸다 — 별도 누산기를 두면 화면의 초와
      // 리포트의 초가 조용히 갈린다. 프레임마다 전체를 다시 훑지만 30초 테이크가
      // ~1400프레임이라 비용은 무시할 수준이고, rAF tick이 아니라 여기서 계산해야
      // liveRef의 객체 동일성이 유지돼 값이 안 변할 때 리렌더가 안 난다.
      liveRef.current = {
        // 프레임 아닌 이벤트의 흔적은 그대로 들고 간다 — 위에서 센 값이다.
        interruptCount: liveRef.current.interruptCount,
        discontinuityCount: liveRef.current.discontinuityCount,
        lastEvent: liveRef.current.lastEvent,
        // **분석 창 안에서만 센다.** 화면이 29초라고 말하는 동안 리포트로 넘어가는 것은
        // 마지막 20초뿐이면, 사용자가 본 숫자와 저장된 값이 갈린다(`stop()`이 같은
        // 함수로 자른다). 두 자리가 같은 창을 봐야 그 상태가 없다.
        voicedSec: voicedSpanSec(withinAnalysisWindow(framesRef.current)),
        points: appendLivePoint(liveRef.current.points, frame),
        sung: growSungRange(liveRef.current.sung, frame),
        // `markTake()` 뒤 첫 프레임에서만 정해지고 테이크 내내 안 변한다 — 채점 앵커
        // (`frames[0]`)와 같은 값이어야 한다.
        takeStartMs: liveRef.current.takeStartMs ?? frame.timestampMs,
        // 재개 직후 첫 프레임에서만 찍힌다.
        resumedAtMs: resumeMarkPendingRef.current
          ? frame.timestampMs
          : liveRef.current.resumedAtMs,
        // 엔진은 들었는데 게이트가 버렸다 = 이 방 기준으로 너무 작다.
        faintAtMs:
          event.frame.voiced && !gated.isVoice
            ? event.frame.timestampMs
            : liveRef.current.faintAtMs,
        floorLocked: gated.state.floorDb != null,
        noiseFloorDbfs: gated.state.floorDb,
        // 게이트 **앞**의 rms. 오버레이가 통과선과 나란히 그린다.
        lastRmsDbfs: Number.isFinite(event.frame.rmsDb)
          ? event.frame.rmsDb
          : liveRef.current.lastRmsDbfs,
        // 게이트 **앞의** 프레임을 본다 — 묻는 것이 "엔진이 보내주나"이지 "이 프레임이
        // 채택됐나"가 아니다.
        smoothedSeen:
          liveRef.current.smoothedSeen || event.frame.smoothedF0Hz != null,
        probe: stepProbeLatch(liveRef.current.probe, {
          tMs: event.frame.timestampMs,
          engineVoiced: event.frame.voiced,
          isVoice: gated.isVoice,
          f0Hz: event.frame.f0Hz,
          smoothedF0Hz: event.frame.smoothedF0Hz ?? null,
        }),
      };
      resumeMarkPendingRef.current = false;
    },
    [resetAccumulators],
  );

  // 프레임을 매번 setState 하지 않는다(#9 패턴): 최신 값은 ref에 모으고 rAF로 ~30fps throttle.
  useRafThrottle(() => setLive(liveRef.current));

  const teardown = useCallback((): void => {
    transportRef.current?.stop();
    transportRef.current = null;
  }, []);

  const pause = useCallback((): void => {
    pausedRef.current = true;
    pcmRef.current.detach();
  }, []);

  const resume = useCallback((): void => {
    pausedRef.current = false;
    resumeMarkPendingRef.current = true;
    if (micGraphRef.current) {
      pcmRef.current.attach(micGraphRef.current);
    }
  }, []);

  // 카운트다운 동안 모인 것은 방 소리다 — 프레임·곡선·표본은 버리고, 게이트가 잰 바닥과
  // 세션 계측(끊김·불연속 수)은 들고 간다. `takeStartMs`는 다음 프레임이 채운다.
  const markTake = useCallback((): void => {
    framesRef.current = [];
    pcmRef.current.reset();
    liveRef.current = {
      ...INITIAL_LIVE,
      floorLocked: liveRef.current.floorLocked,
      noiseFloorDbfs: liveRef.current.noiseFloorDbfs,
      lastRmsDbfs: liveRef.current.lastRmsDbfs,
      smoothedSeen: liveRef.current.smoothedSeen,
      interruptCount: liveRef.current.interruptCount,
      discontinuityCount: liveRef.current.discontinuityCount,
      lastEvent: liveRef.current.lastEvent,
    };
    setLive(liveRef.current);
  }, []);

  const start = useCallback(
    (options: StartOptions = {}): void => {
      // 열려 있던 전송은 먼저 닫는다 — 안 닫으면 `transportRef` 를 덮어써 네이티브 세션과
      // 리스너가 고아로 남는다(StrictMode 의 이중 마운트가 카운트다운 효과를 두 번 돌린다).
      teardown();
      pausedRef.current = false;
      resumeMarkPendingRef.current = false;
      setError(null);
      setLive(INITIAL_LIVE);
      liveRef.current = INITIAL_LIVE;
      resetAccumulators();

      // 기본 config(smoothing light, 65–1100Hz) + **이 세션은 원음을 남긴다**.
      // `recordToFile`은 계약상 기본 off이고(privacy §3), 진단은 "내 목소리 데이터"를
      // 산출물로 요구하는 흐름이라 여기서 명시적으로 켠다. 연습·디버그 세션은 안 켠다.
      // `backingTrack`은 반주가 같이 나가는 세그먼트(노래)만 켠다 — 네이티브가 그때만
      // 재생이 들리는 오디오 세션 모드로 잡는다(AudioInputConfig 주석).
      const base = createPitchTrackerConfig();
      const config = {
        ...base,
        audio: { ...base.audio, backingTrack: options.backingTrack === true },
        output: { ...base.output, recordToFile: true },
      };

      if (isRunningInNativeApp()) {
        // 여기서 'native'로 못 박지 않는다 — 네이티브 모듈이 등록돼 있지 않으면 모바일
        // 쪽이 합성 모듈로 떨어지고, 그건 `session_started`가 와야 알 수 있다.
        const off = native.on('pitchTrackerEvent', onEvent);
        transportRef.current = {
          stop: () => {
            void native.invoke('stopPractice').catch(() => undefined);
            off();
          },
        };
        // **권한 시트는 네이티브 `start()` 가 스스로 띄운다**(ADR-0004). 그 앞에
        // `requestPermission` 을 하나 더 두면 허용된 마이크를 거절할 수 있고 실제로
        // 그랬다 — `apps/mobile/src/utils/practice.ts` 의 경고가 그 얘기다.
        //
        // 그래서 고치는 자리는 **거절의 해석**이다. 예전에는 어떤 거절이든 'denied'
        // 였는데, 그 거절에는 두 가지가 섞여 있다: 사용자가 정말 거부한 것과, 방금
        // 허용된 세션이 아직 못 열린 것. 후자를 권한 거부로 적으면 "허용"을 누른
        // 사용자가 권한 화면에 갇힌다(2026-09-16 iPhone 17 Pro 실측).
        //
        // 무엇이었는지는 **물어서** 정한다. `getPermissionStatus` 는 읽기만 하므로
        // 시트를 두 번 띄우지 않는다. 허용돼 있는데 거절이었다면 일시적인 것이라
        // 한 번 더 연다.
        const classify = (): void => {
          void native
            .invoke('getPermissionStatus', { kind: 'microphone' })
            .then(({ status }) =>
              setError(status === 'blocked' ? 'blocked' : 'denied'),
            )
            .catch(() => setError('unsupported'));
        };
        void native.invoke('startPractice', config).catch(() => {
          void native
            .invoke('getPermissionStatus', { kind: 'microphone' })
            .then(({ status }) => {
              if (status !== 'granted') {
                setError(status === 'blocked' ? 'blocked' : 'denied');
                return;
              }
              // 권한은 있는데 못 열었다 — 한 번만 다시. 두 번째도 실패하면 엔진 문제다.
              void native
                .invoke('startPractice', config)
                .catch(() => setError('unsupported'));
            })
            .catch(classify);
        });
        return;
      }

      // 브라우저: **진짜 마이크**를 연다. 예전엔 여기서 곧바로 mock으로 떨어졌는데, mock은
      // 고정 음높이·confidence 0.95·−18dBFS를 내므로 `nx serve web`에서는 어떤 목소리도
      // 화면에 닿지 않았다 — 측정 화면을 눈으로 확인할 방법 자체가 없었다.
      // 소스 모듈은 디버그 콘솔이 쓰던 것과 **같은 것**이다(`app/audio`). 추정기는 JS ACF라
      // 네이티브 MPM과 다르고, 그래서 출처를 'mic'으로 따로 남긴다.
      // 마이크 실패는 모듈이 `error` 이벤트로 알린다(→ 에러 화면). 여기 플래그는 그
      // 경우에 두 번 알리지 않으려고만 쓴다.
      let micFailed = false;
      const module = createBrowserSourceModule({
        getSource: () => 'mic',
        onMicFailure: () => {
          micFailed = true;
        },
        onMicOpen: (graph) => {
          // 재개할 때 다시 붙일 대상이라 붙잡아 둔다.
          micGraphRef.current = graph;
          pcmRef.current.attach(graph);
        },
        onMicClose: () => {
          micGraphRef.current = null;
          pcmRef.current.detach();
        },
        preferredSampleRate: PCM_TARGET_SAMPLE_RATE,
      });
      const bridge = createPracticeBridge(onEvent, module);
      transportRef.current = {
        stop: () => {
          void bridge.stopPractice();
          bridge.dispose();
        },
      };
      void bridge.startPractice(config).catch(() => {
        // 마이크를 못 연 것은 `onMicFailure`가 이미 알렸고, 그때는 합성 세션이 열려
        // 출처가 mock으로 적힌다. 마이크 실패가 아닌데 시작이 깨졌다면 진짜 오류다.
        if (!micFailed) {
          setError('unsupported');
        }
      });
    },
    [onEvent, resetAccumulators, teardown],
  );

  // 복사본을 낸다 — 수집 버퍼를 그대로 넘기면 teardown 직후 늦게 도착한 프레임이
  // 호출자가 이미 쥔 배열에 append되어 측정값이 사후에 바뀔 수 있다.
  // 분석 창 밖(마지막 20초보다 앞)은 여기서 잘라 낸다 — 리포트가 보는 프레임과 파일로
  // 나가는 원음이 **같은 구간**이어야 한다. 브라우저 PCM 은 `markTake` 가 같이 비우므로
  // 그대로 참이고, 네이티브 wav 는 세션 시작(카운트다운 진입)부터 쓰므로 20초 미만 테이크
  // 파일 앞에 카운트다운 ~3초가 남는다 — 알려진 어긋남, 파일은 채점에 안 쓰인다.
  const stop = useCallback((): NativeF0Frame[] => {
    const frames = withinAnalysisWindow(framesRef.current.slice());
    teardown();
    return frames;
  }, [teardown]);

  // teardown이 녹음기를 뗀 뒤에도 모은 표본은 남는다 — stop() 다음에 불러야 테이크
  // 전체가 들어온다.
  const takePcm = useCallback((): PcmTake | null => pcmRef.current.take(), []);
  // state 지연 없이 ref를 본다 — `stop()` 직후 부르는 자리라 `live`는 한 틱 늦다.
  const readNoiseFloorDbfs = useCallback(
    (): number | null => liveRef.current.noiseFloorDbfs,
    [],
  );

  // 네이티브에서만 답이 온다. 브라우저·mock에서는 RPC가 없거나 null이라 조용히 null이다 —
  // 여기서 던지면 원음이 없는 경로에서 진단 완료 자체가 실패한다.
  const takeNativeRecording =
    useCallback(async (): Promise<NativeRecording | null> => {
      if (!isRunningInNativeApp()) {
        return null;
      }
      try {
        return await native.invoke('getLastRecording');
      } catch {
        return null;
      }
    }, []);

  // 끝난 테이크의 **바이트**를 가져온다. 경로만으로는 웹이 파일을 못 읽는다(https origin).
  // 브라우저·mock 경로에는 줄 파일이 없어 곧바로 null 이다.
  const readNativeRecording = useCallback(
    async (uri: string): Promise<Uint8Array | null> =>
      isRunningInNativeApp() ? readTakeBytes(nativeReadDeps(), uri) : null,
    [],
  );

  /**
   * 기기에 남은 테이크 파일을 지운다. 업로드가 끝나면 그 파일은 더 쓸 데가 없는데, 지우는
   * 쪽을 아무도 안 부르면 회차마다 쌓인다(privacy §3 은 "사용자가 개별/전체 삭제 가능"을
   * 요구하고 RPC 도 있었지만 웹에 소비자가 없었다). 없는 경로도 오류가 아니다(멱등).
   */
  const deleteNativeRecording = useCallback(
    async (uri: string): Promise<void> => {
      if (!isRunningInNativeApp()) {
        return;
      }
      try {
        await native.invoke('deleteRecording', { uri });
      } catch {
        /* 못 지워도 진단과는 무관하다 */
      }
    },
    [],
  );

  const reset = useCallback((): void => {
    teardown();
    resetAccumulators();
    liveRef.current = INITIAL_LIVE;
    setLive(INITIAL_LIVE);
    setError(null);
    setSource(null);
  }, [teardown, resetAccumulators]);

  // 언마운트 시 세션/리스너 정리(녹음 중 이탈).
  useEffect(() => () => teardown(), [teardown]);

  return {
    live,
    error,
    source,
    readNoiseFloorDbfs,
    start,
    stop,
    takePcm,
    takeNativeRecording,
    readNativeRecording,
    deleteNativeRecording,
    pause,
    resume,
    markTake,
    reset,
  };
};
